import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import app from '../../main' // import the instance
import checkResponse from '@/utils/checkResponse'
import parserFilters from '@/utils/parserFilters'
import parserSlug from '@/utils/parserSlug'
import { camelCase } from 'change-case'

Vue.use(Vuex)

const state = {
  products: [],
  topSuppliers: [],
  pagination: {
    currentPage: 1,
    perPage: 5,
    totalProducts: 0,
    totalPages: 0
  },
  productsCatalog: {
    unitsMeasurement: [],
    currencies: [],
    productCategories: []
  },
  filterParams: '',
  isModalVisible: false,
  isModalTopSuppliersVisible: false,
  isModalAttachVisible: false
}

const mutations = {
  SET_PRODUCTS (state, payload) {
    state.products = payload.data

    state.pagination = {
      currentPage: payload.current_page,
      perPage: payload.per_page,
      totalProducts: payload.total,
      totalPages: payload.last_page
    }
  },
  SET_TOP_SUPPLIERS (state, payload) {
    state.topSuppliers = payload.data
  },
  ADD_PRODUCT (state, payload) {
    state.products.unshift(payload)
  },
  UPDATE_PRODUCT (state, payload) {
    const idx = state.products.findIndex(u => u.id === payload.id)

    if (idx >= 0) {
      Vue.set(state.products, idx, payload)
    }
  },
  DELETE_PRODUCT (state, payload) {
    const idx = state.products.findIndex(u => u.id === payload.id)

    if (idx >= 0) {
      Vue.delete(state.products, idx)
    }
  },
  DELETE_PRODUCTS (state, payload) {
    for (let id of payload.ids) {
      const idx = state.products.findIndex(u => u.id === id)

      if (idx >= 0) {
        Vue.delete(state.products, idx)
      }
    }
  },
  SET_MODAL_VISIBLE (state, payload) {
    state.isModalVisible = payload
  },
  SET_FILTER_PARAMS (state, payload) {
    state.filterParams = payload
  },
  SET_CATALOG (state, { object, payload }) {
    state.productsCatalog[object] = payload.data
  },
  SET_MODAL_CATEGORY_VISIBLE (state, payload) {
    state.isModalCategoryVisible = payload
  },
  SET_MODAL_TOP_SUPPLIERS_VISIBLE (state, payload) {
    state.isModalTopSuppliersVisible = payload
  },
  SET_MODAL_ATTACH_VISIBLE (state, payload) {
    state.isModalAttachVisible = payload
  }
}

const actions = {
  async loadProducts ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.get(`products?page=${payload.page}${state.filterParams}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_PRODUCTS', response.data)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async addProduct ({ commit }, payload) {
    let formData = new FormData()
    formData.append('sku', payload.form.sku)
    formData.append('name', payload.form.name)
    formData.append('description', payload.form.description)
    formData.append('number_part', payload.form.number_part)
    formData.append('price', payload.form.price)
    formData.append('product_category_id', payload.form.product_category_id)
    formData.append('unit_measurement_id', payload.form.unit_measurement_id)
    formData.append('currency_id', payload.form.currency_id)
    formData.append('tech_sheet', payload.tech_sheet)
    formData.append('security_sheet', payload.security_sheet)

    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })

    try {
      const response = await axios.post(`products`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('ADD_PRODUCT', response.data)
        commit('SET_MODAL_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.products', text: 'front.added_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async editProduct ({ commit }, payload) {
    const product = {
      sku: payload.form.sku,
      name: payload.form.name,
      description: payload.form.description,
      number_part: payload.form.number_part,
      price: payload.form.price,
      product_category_id: payload.form.product_category_id,
      unit_measurement_id: payload.form.unit_measurement_id,
      currency_id: payload.form.currency_id
    }

    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })

    try {
      const response = await axios.put(`products/${payload.form.id}`, product)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('UPDATE_PRODUCT', response.data)
        commit('SET_MODAL_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.products', text: 'front.updated_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async deleteProduct ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.delete(`products/${payload.id}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('DELETE_PRODUCT', payload)
        commit('SET_MODAL_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.products', text: 'front.deleted_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async deleteProducts ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.delete(`products/delete`, { params: { ids: payload.ids } })
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('DELETE_PRODUCTS', payload)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.products', text: 'front.deleted_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async actionProduct ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.put(`products/${payload.id}/${payload.action}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('UPDATE_PRODUCT', response.data)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.products', text: `products.${payload.action}`, icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  printReport ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      window.open(axios.defaults.baseURL + '/products/export?' + state.filterParams)
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  applyFilter ({ commit }, payload) {
    const filter = parserFilters(payload)
    commit('SET_FILTER_PARAMS', filter)
  },
  clearFilter ({ commit }, payload) {
    const filter = ''
    commit('SET_FILTER_PARAMS', filter)
  },
  async loadCatalog ({ commit }, payload) {
    let url = parserSlug(payload.url)
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.get(`${url}${payload.filter}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_CATALOG', { object: camelCase(payload.url), payload: response.data })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async loadTopSuppliers ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      const response = await axios.get(`products/${payload.id}/top-suppliers`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_TOP_SUPPLIERS', response.data)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  async addAttachments ({ commit }, payload) {
    let formData = new FormData()
    formData.append('id', payload.form.id)
    formData.append('tech_sheet', payload.tech_sheet)
    formData.append('security_sheet', payload.security_sheet)

    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })

    try {
      const response = await axios.post(`products/${payload.form.id}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('UPDATE_PRODUCT', response.data)
        commit('SET_MODAL_ATTACH_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.products', text: 'front.added_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  },
  setModalVisible ({ commit }, payload) {
    commit('SET_MODAL_VISIBLE', payload)
  },
  setModalImportVisible ({ commit }, payload) {
    commit('SET_MODAL_IMPORT_VISIBLE', payload, { root: true })
  },
  setModalCategoryVisible ({ commit }, payload) {
    commit('SET_MODAL_CATEGORY_VISIBLE', payload)
  },
  setModalTopSuppliersVisible ({ commit }, payload) {
    commit('SET_MODAL_TOP_SUPPLIERS_VISIBLE', payload)
  },
  setModalAttachVisible ({ commit }, payload) {
    commit('SET_MODAL_ATTACH_VISIBLE', payload)
  },
  async download ({ commit }, payload) {
    app.$Progress.start()
    commit('SET_LOADING', true, { root: true })
    try {
      window.open(`${Vue.axios.defaults.baseURL}/attachments/${payload.id}/download`)
    } catch (error) {
      app.$Progress.fail()
    } finally {
      app.$Progress.finish()
      commit('SET_LOADING', false, { root: true })
    }
  }
}

const getters = {
  products: state => state.products,
  topSuppliers: state => state.topSuppliers,
  productsCatalog: state => state.productsCatalog,
  pagination: state => state.pagination,
  isModalVisible: state => state.isModalVisible,
  isModalTopSuppliersVisible: state => state.isModalTopSuppliersVisible,
  isModalAttachVisible: state => state.isModalAttachVisible,
  filterParams: state => state.filterParams
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
